// The Messages tab (#333): the reader's conversations, last message first, each with its unread
// count, and the open conversation's thread. A wide screen shows the list beside the thread; a
// phone shows one at a time, with a way back to the list.
//
// initialConversationId: the conversation to open first (just started from a booking).
// onUnreadChange: receives the reader's unread total after each load, for the tab label (MS-3).
import React from 'react';
import { api, isSignInExpired } from '../../lib/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ConversationThread } from './ConversationThread';
import { palette as T, type } from '../../design';

const FONT = "'Plus Jakarta Sans', sans-serif";

export const CONVERSATIONS_LOAD_FAILED = 'We could not load your conversations. Refresh the page to try again.';
export const NO_CONVERSATIONS = 'You have no conversations yet. To start one, open a booking and tap Message.';

function formatTime(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const unreadText = (n) => `${n} unread`;

function ConversationRow({ conversation, active, onOpen }) {
  const name = conversation.with?.name || 'Conversation';
  const unread = conversation.unread || 0;
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(conversation.id)}
        aria-current={active ? 'true' : undefined}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
          border: 'none', borderLeft: '3px solid ' + (active ? T.terracotta : 'transparent'),
          background: active ? T.ivory : T.white, cursor: 'pointer', textAlign: 'left', fontFamily: FONT,
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: type.body, fontWeight: unread ? 700 : 600, color: T.espresso, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          <span style={{ display: 'block', fontSize: type.label, color: T.driftwood }}>{formatTime(conversation.lastMessageAt)}</span>
        </span>
        {unread > 0 && (
          <span style={{ flexShrink: 0, background: T.terracotta, color: T.white, borderRadius: 9999, padding: '2px 8px', fontSize: type.caption, fontWeight: 700 }}>
            {unreadText(unread)}
          </span>
        )}
      </button>
    </li>
  );
}

export function MessagesPanel({ initialConversationId = null, onUnreadChange }) {
  const isMobile = useIsMobile();
  const [conversations, setConversations] = React.useState(null);
  const [loadError, setLoadError] = React.useState('');
  const [openId, setOpenId] = React.useState(initialConversationId);
  const onUnreadRef = React.useRef(onUnreadChange);
  onUnreadRef.current = onUnreadChange;

  const load = React.useCallback(async () => {
    try {
      const data = await api.get('/conversations');
      setConversations(data?.conversations || []);
      setLoadError('');
      onUnreadRef.current?.(data?.unreadTotal || 0);
    } catch (err) {
      setConversations((list) => list || []);
      // An ended sign-in already took them to the login page with its own notice (#502). Any
      // other failure, a server problem while renewing the sign-in included, is said (#507).
      if (isSignInExpired(err)) return;
      console.error('[messages] could not load the conversations', err?.status ?? err);
      setLoadError(CONVERSATIONS_LOAD_FAILED);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const list = conversations || [];
  const showList = !isMobile || !openId;
  const showThread = Boolean(openId);

  return (
    <div style={{ fontFamily: FONT }}>
      <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: '0 0 24px' }}>Messages</h2>

      {loadError && (
        <p role="alert" style={{ fontSize: type.body, color: T.errorRed, margin: '0 0 16px' }}>{loadError}</p>
      )}

      {conversations !== null && list.length === 0 && !loadError && !openId && (
        <div style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '32px 20px', textAlign: 'center' }}>
          <p style={{ fontSize: type.body, color: T.driftwood, margin: 0 }}>{NO_CONVERSATIONS}</p>
        </div>
      )}

      {(list.length > 0 || openId) && (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
          {showList && list.length > 0 && (
            <nav aria-label="Conversations" style={{ width: isMobile ? '100%' : 280, flexShrink: 0, background: T.white, borderRadius: 16, border: '1px solid ' + T.border, overflow: 'hidden' }}>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {list.map((c) => (
                  <ConversationRow key={c.id} conversation={c} active={c.id === openId} onOpen={setOpenId} />
                ))}
              </ul>
            </nav>
          )}
          {showThread && (
            <div style={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
              {isMobile && (
                <button type="button" onClick={() => setOpenId(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: type.body, fontWeight: 600, color: T.terracotta, padding: 0, margin: '0 0 12px' }}>
                  ← All conversations
                </button>
              )}
              <ConversationThread conversationId={openId} onRead={load} />
            </div>
          )}
          {!showThread && !isMobile && (
            <p style={{ flex: 1, fontSize: type.body, color: T.driftwood, margin: '12px 0 0' }}>Pick a conversation to read it and reply.</p>
          )}
        </div>
      )}
    </div>
  );
}
